import { cn } from "./classNames-BzrkDnOD.js";
import { useArgoDesign } from "./context-U293r3v-.js";
import { Loader2 } from "./icons-wcA70iQx.js";
import { Root, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Button/styles.ts
const variants = cva([
	"tailwindcss-preflight",
	"tw:group/button",
	"tw:border-box",
	"tw:relative",
	"tw:flex",
	"tw:items-center",
	"tw:justify-center",
	"tw:gap-2",
	"tw:text-xs",
	"tw:leading-5",
	"tw:font-medium",
	"tw:[[data-brand^=keboola-legacy]_&]:tracking-chrome",
	"tw:[[data-brand^=keboola-legacy]_&]:uppercase",
	"tw:rounded",
	"tw:border",
	"tw:border-transparent",
	"tw:shadow-none",
	"tw:[a]:cursor-pointer",
	"tw:focus-visible:outline",
	"tw:focus-visible:outline-1",
	"tw:focus-visible:-outline-offset-1",
	"tw:focus-visible:outline-ring",
	"tw:focus-visible:ring-4",
	"tw:focus-visible:ring-ring/30",
	"tw:disabled:opacity-40",
	"tw:disabled:cursor-not-allowed",
	"tw:!no-underline",
	"tw:aria-disabled:opacity-40",
	"tw:aria-disabled:cursor-not-allowed",
	"tw:[&_svg]:shrink-0"
], {
	defaultVariants: {
		size: "medium",
		variant: "primary",
		block: false
	},
	variants: {
		block: { true: ["tw:w-full"] },
		variant: {
			primary: [
				"tw:text-white",
				"tw:bg-primary-500",
				"tw:focus:text-white",
				"tw:hover:text-white",
				"tw:hover:not-disabled:bg-primary-600",
				"tw:active:not-disabled:bg-primary-800",
				"tw:aria-disabled:!bg-primary-500"
			],
			secondary: [
				"tw:text-white",
				"tw:bg-secondary-500",
				"tw:focus:text-white",
				"tw:hover:text-white",
				"tw:hover:not-disabled:bg-secondary-700",
				"tw:active:not-disabled:bg-secondary-800",
				"tw:aria-disabled:!bg-secondary-500"
			],
			kai: [
				"tw:bg-kai",
				"tw:animate-kai-drift",
				"tw:motion-reduce:animate-none",
				"tw:text-white",
				"tw:hover:text-white",
				"tw:focus:text-white",
				"tw:hover:not-disabled:brightness-110",
				"tw:focus-visible:ring-ring/50"
			],
			danger: [
				"tw:text-white",
				"tw:bg-error-500",
				"tw:focus:text-white",
				"tw:hover:text-white",
				"tw:hover:not-disabled:bg-error-600",
				"tw:active:not-disabled:bg-error-700",
				"tw:aria-disabled:!bg-error-500"
			],
			invisible: [
				"tw:text-foreground",
				"tw:bg-transparent",
				"tw:focus:text-foreground",
				"tw:hover:not-disabled:bg-muted",
				"tw:hover:text-foreground",
				"tw:active:not-disabled:bg-secondary",
				"tw:[&_svg]:text-neutral-400",
				"tw:hover:not-disabled:[&_svg]:text-secondary-600",
				"tw:active:not-disabled:[&_svg]:text-secondary-600",
				"tw:aria-disabled:!bg-transparent"
			],
			outline: [
				"tw:text-foreground",
				"tw:bg-card",
				"tw:!border-input",
				"tw:hover:text-foreground",
				"tw:focus:text-foreground",
				"tw:hover:not-disabled:!border-muted-foreground/60",
				"tw:hover:not-disabled:bg-[color-mix(in_oklab,rgb(var(--muted-foreground))_15%,rgb(var(--card)))]",
				"tw:active:not-disabled:bg-[color-mix(in_oklab,rgb(var(--muted-foreground))_35%,rgb(var(--card)))]",
				"tw:[&_svg]:text-muted-foreground",
				"tw:not-disabled:hover:[&_svg]:text-secondary-600",
				"tw:active:not-disabled:[&_svg]:text-secondary-600",
				"tw:aria-disabled:!bg-card",
				"tw:aria-disabled:!border-input"
			],
			inline: [
				"tw:text-foreground",
				"tw:bg-transparent",
				"tw:rounded-full",
				"tw:[&_svg]:text-muted-foreground",
				"tw:not-disabled:hover:[&_svg]:text-secondary-600",
				"tw:not-disabled:focus:[&_svg]:text-secondary-600"
			]
		},
		size: {
			medium: [
				"tw:max-h-10",
				"tw:min-h-10",
				"tw:px-4",
				"tw:py-2.5",
				"tw:[&_svg:not([class*=\"tw:size-\"])]:size-4"
			],
			small: [
				"tw:max-h-8",
				"tw:min-h-8",
				"tw:px-4",
				"tw:py-1.5",
				"tw:[&_svg:not([class*=\"tw:size-\"])]:size-4"
			],
			large: [
				"tw:max-h-12",
				"tw:min-h-12",
				"tw:px-4",
				"tw:py-3.5",
				"tw:[&_svg:not([class*=\"tw:size-\"])]:size-4"
			],
			"icon-lg": [
				"tw:size-12",
				"tw:p-0",
				"tw:[&_svg:not([class*=\"tw:size-\"])]:size-6"
			],
			icon: [
				"tw:size-10",
				"tw:p-0",
				"tw:[&_svg:not([class*=\"tw:size-\"])]:size-4"
			],
			"icon-sm": [
				"tw:size-8",
				"tw:p-0",
				"tw:[&_svg:not([class*=\"tw:size-\"])]:size-4"
			],
			"icon-xs": [
				"tw:size-6",
				"tw:p-0",
				"tw:[&_svg:not([class*=\"tw:size-\"])]:size-4"
			],
			"icon-2xs": [
				"tw:!inline-flex",
				"tw:size-5",
				"tw:max-h-5",
				"tw:[&_svg:not([class*=\"tw:size-\"])]:size-3"
			],
			"icon-3xs": [
				"tw:h-fit",
				"tw:w-fit",
				"tw:[&_svg:not([class*=\"tw:size-\"])]:size-4"
			]
		}
	}
});
const argoVariants = cva([
	"tailwindcss-preflight",
	"tw:relative",
	"tw:inline-flex",
	"tw:items-center",
	"tw:justify-center",
	"tw:whitespace-nowrap",
	"tw:gap-2",
	"tw:border",
	"tw:border-transparent",
	"tw:rounded-[var(--radius-lg,0.25rem)]",
	"tw:font-medium",
	"tw:select-none",
	"tw:!outline-hidden",
	"tw:focus-visible:ring-2",
	"tw:disabled:cursor-not-allowed",
	"tw:disabled:opacity-50",
	"tw:active:opacity-60",
	"tw:transition-all",
	"tw:!no-underline",
	"tw:aria-disabled:cursor-not-allowed",
	"tw:aria-disabled:opacity-50",
	"tw:shrink-0",
	"tw:[&_svg]:pointer-events-none",
	"tw:[&_svg]:shrink-0",
	"tw:[&_svg:not([class*='tw:size-'])]:size-4"
], {
	defaultVariants: {
		variant: "primary",
		size: "medium"
	},
	variants: {
		block: { true: ["tw:w-full"] },
		variant: {
			primary: [
				"tw:bg-primary",
				"tw:text-primary-foreground",
				"tw:[a]:hover:bg-primary/80",
				"tw:hover:not-disabled:bg-primary/90",
				"tw:hover:text-primary-foreground",
				"tw:focus:text-primary-foreground",
				"tw:focus-visible:border-primary",
				"tw:focus-visible:ring-ring/50"
			],
			secondary: [
				"tw:bg-primary/10",
				"tw:text-primary",
				"tw:hover:not-disabled:bg-primary/20",
				"tw:focus-visible:border-primary",
				"tw:focus-visible:ring-ring/50"
			],
			kai: [
				"tw:bg-kai",
				"tw:animate-kai-drift",
				"tw:motion-reduce:animate-none",
				"tw:text-white",
				"tw:hover:text-white",
				"tw:focus:text-white",
				"tw:hover:not-disabled:brightness-110",
				"tw:focus-visible:ring-ring/50"
			],
			danger: [
				"tw:bg-destructive",
				"tw:text-destructive-foreground",
				"tw:hover:not-disabled:bg-destructive/90",
				"tw:hover:text-destructive-foreground",
				"tw:focus:text-destructive-foreground",
				"tw:focus-visible:border-destructive",
				"tw:focus-visible:ring-destructive/20"
			],
			outline: [
				"tw:!border-input",
				"tw:bg-background",
				"tw:bg-clip-padding",
				"tw:text-foreground",
				"tw:hover:not-disabled:bg-[color-mix(in_oklab,rgb(var(--primary))_10%,rgb(var(--background)))]",
				"tw:hover:not-disabled:!border-muted-foreground",
				"tw:hover:not-disabled:text-primary",
				"tw:hover:active:bg-background",
				"tw:focus-visible:!border-primary",
				"tw:focus-visible:ring-ring/50"
			],
			invisible: [
				"tw:bg-transparent",
				"tw:bg-clip-padding",
				"tw:text-foreground",
				"tw:hover:not-disabled:bg-primary/10",
				"tw:hover:not-disabled:text-primary",
				"tw:hover:not-disabled:border-primary/10",
				"tw:focus-visible:ring-ring/50"
			],
			inline: [
				"tw:bg-transparent",
				"tw:text-primary",
				"tw:hover:underline",
				"tw:hover:active:bg-transparent",
				"tw:focus-visible:ring-ring/50"
			]
		},
		size: {
			medium: [
				"tw:max-h-9",
				"tw:py-2",
				"tw:px-4",
				"tw:text-sm"
			],
			small: [
				"tw:max-h-8",
				"tw:py-2",
				"tw:px-3",
				"tw:text-xs"
			],
			large: [
				"tw:max-h-10",
				"tw:py-2.5",
				"tw:px-6",
				"tw:text-sm"
			],
			"icon-lg": ["tw:size-10"],
			icon: ["tw:size-9"],
			"icon-sm": ["tw:size-8"],
			"icon-xs": ["tw:size-6"],
			"icon-2xs": [],
			"icon-3xs": []
		}
	}
});
//#endregion
//#region src/components/Button/Button.tsx
/**
* Any user-initiated action; `size="icon-*"` with no children is the icon-only button.
*
* - Not for: inline text links (`ButtonInline`), URL navigation (`Link`), action + dropdown (`SplitButton`).
* - Wrap in `Tooltip` when icon-only or disabled; `Popconfirm` for destructive actions.
*
* @see RECIPES.md#disabled-button-tooltip
* @see RECIPES.md#confirm-dialog
*/
const Button = ({ ref, children, disabled = false, className, type = "button", isLoading = false, asChild = false, onClick, variant = "primary", size = "medium", block = false, ...buttonProps }) => {
	const renderArgo = useArgoDesign();
	const Comp = asChild ? Root : "button";
	const isIconOnly = String(size).startsWith("icon");
	const classNames = renderArgo ? argoVariants({
		variant,
		size,
		block,
		className
	}) : variants({
		size,
		variant,
		block,
		className
	});
	const handleClick = (event) => {
		if (event.currentTarget.tagName === "A" && disabled) {
			event.preventDefault();
			return;
		}
		onClick?.(event);
	};
	return /* @__PURE__ */ jsxs(Comp, {
		...buttonProps,
		ref,
		disabled,
		"aria-disabled": buttonProps["aria-disabled"] ?? disabled,
		"data-variant": variant,
		"data-icon-only": isIconOnly,
		type,
		onClick: handleClick,
		className: cn(classNames),
		children: [isLoading && /* @__PURE__ */ jsx(Loader2, { className: "tw:size-4 tw:shrink-0 tw:animate-spin" }), /* @__PURE__ */ jsx(Slottable, { children: isLoading && isIconOnly ? null : children })]
	});
};
//#endregion
//#region src/components/Button/ButtonInline.tsx
const buttonInlineVariants = cva([
	"tailwindcss-preflight",
	"tw:inline-flex",
	"tw:items-center",
	"tw:gap-2",
	"tw:underline-offset-2",
	"tw:disabled:opacity-40",
	"tw:disabled:cursor-not-allowed",
	"tw:disabled:text-neutral-500",
	"tw:disabled:no-underline",
	"tw:disabled:[&_svg]:text-neutral-400",
	"tw:[&_svg.lucide:not([class*='tw:size-'])]:size-4",
	"tw:[&_svg]:shrink-0"
], {
	defaultVariants: {
		variant: "link",
		color: "link"
	},
	variants: {
		color: {
			link: [
				"tw:text-primary",
				"tw:hover:text-primary/90",
				"tw:[&_svg]:text-primary"
			],
			dark: ["tw:text-foreground tw:[&_svg]:text-muted-foreground"],
			danger: ["tw:text-error-500 tw:[&_svg]:text-error-500"],
			muted: ["tw:text-muted-foreground tw:[&_svg]:text-muted-foreground"],
			custom: []
		},
		variant: {
			link: ["tw:underline", "tw:hover:no-underline"],
			"link-reverse": [
				"tw:no-underline",
				"",
				"tw:hover:underline"
			],
			custom: []
		}
	}
});
const ButtonInline = ({ ref, children, className, variant, color, type = "button", asChild = false, ...buttonProps }) => {
	return /* @__PURE__ */ jsx(asChild ? Root : "button", {
		...buttonProps,
		ref,
		type: asChild ? void 0 : type,
		className: buttonInlineVariants({
			variant,
			color,
			className
		}),
		children
	});
};
//#endregion
export { Button, ButtonInline, argoVariants };

//# sourceMappingURL=Button-B21oVcPU.js.map